import { useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { Organization as OrganizationIcon } from '@factorialco/f0-react/icons/app'
import { NodeProps } from '@xyflow/react'

import i18n from 'modules/core/lib/i18n'

import { And } from 'modules/workflows/components/Partials/And'
import { Operator } from 'modules/workflows/components/Partials/Operator'
import {
  EntityResolution,
  VariableSelectorConfig,
} from 'modules/workflows/components/Sidepanel/VariableSelectorTypes'
import { useWorkflowContext } from 'modules/workflows/contexts/WorkflowContext'

import { NodeUI, isNodePropsConditionIf } from './types'

/**
 * Parses a value string to extract IDs.
 * Handles both single values ("4") and JSON arrays ("[4,5]")
 */
function parseValueToIds(value: string): string[] {
  if (!value) return []

  // Try to parse as JSON array
  if (value.startsWith('[')) {
    try {
      const parsed = JSON.parse(value)
      if (Array.isArray(parsed)) {
        return parsed.map(String)
      }
    } catch {
      // Not valid JSON, treat as single value
    }
  }

  // Single value
  return [value]
}

/**
 * Formats a list of items with localized "or" conjunction.
 * e.g. ["a", "b", "c"] => "a, b or c"
 */
function formatListWithOr(items: string[]): string {
  if (items.length === 0) return ''
  if (items.length === 1) return items[0] ?? ''
  if (items.length === 2) return `${items[0]} ${i18n.t('common.or')} ${items[1]}`

  const allButLast = items.slice(0, -1).join(', ')
  const last = items[items.length - 1]
  return `${allButLast} ${i18n.t('common.or')} ${last}`
}

/**
 * Formats a value for display, resolving entity IDs to names when possible.
 */
function formatDisplayValue(
  value: string,
  selectorConfig: VariableSelectorConfig | undefined,
  entityResolution: EntityResolution
): string {
  if (!selectorConfig) return value

  // People group target: delegate to consumer-provided formatter
  if (selectorConfig.type === 'people_group_target') {
    return selectorConfig.formatDisplayValue(value, entityResolution)
  }

  if (selectorConfig.type === 'entity') {
    const ids = parseValueToIds(value)
    const resolutionMap = entityResolution[selectorConfig.entity]
    const names = ids.map((id) => resolutionMap?.[id] || id)
    return formatListWithOr(names) || value
  }

  // For non-entity types, parse array values and format with "or"
  if (value.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(value)
      if (Array.isArray(parsed)) {
        return formatListWithOr(parsed.map(String))
      }
    } catch {
      // Not valid JSON, return as-is
    }
  }

  return value
}

export const FlowNodeSwitchCondition = (props: NodeProps<NodeUI>) => {
  const { variablesConfig, entityResolution } = useWorkflowContext()
  const [isHovered, setIsHovered] = useState(false)

  if (!isNodePropsConditionIf(props)) return
  const comparison = props.data.comparison
  if (!comparison) return null

  const expressions = comparison.expression?.root?.expressions ?? []

  return (
    <Box
      position="relative"
      flexDirection="row"
      width="s360"
      alignItems="flexStart"
      justifyContent="spaceBetween"
      borderRadius={{ all: 'abs012' }}
      background="white"
      border={{ all: { style: 'solid', color: 'grey400', width: 's2' } }}
      style={{ borderColor: isHovered ? '#8ADCE3' : '#CDECEF' }}
      padding="s12"
      gap="s8"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box flexDirection="column" alignItems="flexStart" justifyContent="center" gap="s8">
        {expressions.length === 0 && (
          <Box flexDirection="row" gap="s4">
            <Text color="textCritical" weight="medium" size="200">
              {i18n.t('workflows.node_type.condition.no_expressions')}
            </Text>
          </Box>
        )}
        {expressions.map((expression, index) => {
          // Get label from variable selector config, fallback to capitalized name
          const selectorConfig = variablesConfig[expression.left.name]
          const leftName =
            selectorConfig?.label ||
            expression.left.name.charAt(0).toUpperCase() + expression.left.name.slice(1)

          const unit = selectorConfig?.type === 'number' ? selectorConfig.unit : undefined

          // Format the display value, resolving entity IDs to names
          const displayValue = formatDisplayValue(
            expression.right.value,
            selectorConfig,
            entityResolution
          )

          return (
            <Box
              flexDirection="row"
              flexWrap="wrap"
              gap="s4"
              style={{ maxWidth: '290px' }}
              key={index}
            >
              {index !== 0 && comparison.expression?.root?.operator === 'and' && <And />}
              <Text color="textPrimary" weight="medium" size="200">
                {leftName}
              </Text>
              <Operator operator={expression.operator} />
              <Text weight="medium" size="200">
                {displayValue}
                {unit && ` ${unit}`}
              </Text>
            </Box>
          )
        })}
      </Box>

      <Box
        position="absolute"
        paddingX="s4"
        paddingY="s2"
        height="min-content"
        borderRadius={{ all: 'abs010' }}
        border={{ all: { style: 'solid', color: 'grey400', width: 's1' } }}
        flexDirection="row"
        alignItems="center"
        gap="s2"
        style={{ top: '12px', right: '12px' }}
      >
        <OrganizationIcon width={16} height={16} color="textPrimary" />
        <Text size="100" color="textPrimary" weight="medium">
          {comparison.priority}
        </Text>
      </Box>
    </Box>
  )
}
